import { ref, computed, onMounted, onUnmounted } from 'vue'
import { networkService, type NetworkStatus } from '@/services/networkService'
import { syncService } from '@/services/syncService'
import { offlineStorageService } from '@/services/offlineStorageService'

export function useOfflineSync() {
    const isOnline = ref(navigator.onLine)
    const networkStatus = ref<NetworkStatus>('online')
    const isSyncing = computed(() => syncService.syncing.value)
    const pendingCount = ref(0)
    const lastSyncTime = computed(() => syncService.lastSync.value)
    const syncErrors = computed(() => syncService.errors.value)

    let unsubscribe: (() => void) | null = null

    const updatePendingCount = async () => {
        pendingCount.value = await offlineStorageService.getPendingCount()
    }

    const handleNetworkChange = (status: NetworkStatus) => {
        networkStatus.value = status
        isOnline.value = status !== 'offline'

        // Update pending count when network status changes
        updatePendingCount()
    }

    const manualSync = async () => {
        await syncService.syncPendingTransactions()
        await updatePendingCount()
    }

    const retryFailed = async () => {
        await syncService.retryFailedTransactions()
        await updatePendingCount()
    }

    onMounted(() => {
        // Subscribe to network status changes
        unsubscribe = networkService.onStatusChange(handleNetworkChange)

        // Initial pending count
        updatePendingCount()

        // Update pending count periodically
        const interval = setInterval(updatePendingCount, 10000) // Every 10 seconds

        onUnmounted(() => {
            clearInterval(interval)
        })
    })

    onUnmounted(() => {
        if (unsubscribe) {
            unsubscribe()
        }
    })

    return {
        isOnline,
        networkStatus,
        isSyncing,
        pendingCount,
        lastSyncTime,
        syncErrors,
        manualSync,
        retryFailed,
        updatePendingCount
    }
}
